/**
 * HistoryEntryCard Component
 *
 * Card for displaying a single history log entry (diagnosis, medication, or procedure).
 * Extends the SummaryEntryCard pattern with:
 * - Multi-document source indicator
 * - Coordinator notes display/edit
 * - Merge badge (is_merged_duplicate / merged_from_ids)
 * - AI-generated badge
 * - Diagnosis link indicator (for medications)
 */

import { useState } from 'react';
import {
  CheckCircle,
  Edit2,
  Eye,
  FileText,
  AlertTriangle,
  MessageSquare,
  GitMerge,
  Bot,
  Link2,
  Trash2,
  Save,
  X,
  Strikethrough,
  Undo2,
} from 'lucide-react';
import type {
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  ConfidenceLevel,
  SourcePage,
} from '../../types';

export type HistoryEntryType = 'diagnosis' | 'medication' | 'procedure';
export type HistoryEntry = HistoryDiagnosis | HistoryMedication | HistoryProcedure;

interface HistoryEntryCardProps {
  entry: HistoryEntry;
  entryType: HistoryEntryType;
  onEdit?: (entry: HistoryEntry) => void;
  onVerify?: (entry: HistoryEntry) => void;
  onDelete?: (entry: HistoryEntry) => void;
  onUpdateNotes?: (entry: HistoryEntry, notes: string) => void;
  onStrikeOut?: (entry: HistoryEntry) => void;
  onUndoStrikeOut?: (entry: HistoryEntry) => void;
  onPageClick?: (documentId: string, page: number) => void;
}

// Status badge colors
const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  resolved: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  historical: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  current: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  discontinued: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  prn: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  planned: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  cancelled: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  uncertain: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300' },
};

const CONFIDENCE_COLORS: Record<ConfidenceLevel, { bg: string; text: string }> = {
  high: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300' },
  medium: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300' },
  low: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300' },
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  resolved: 'Resolved',
  historical: 'Historical',
  current: 'Current',
  discontinued: 'Discontinued',
  prn: 'PRN',
  completed: 'Completed',
  planned: 'Planned',
  cancelled: 'Cancelled',
  uncertain: 'Uncertain',
};

function getEntryName(entry: HistoryEntry, entryType: HistoryEntryType): string {
  switch (entryType) {
    case 'diagnosis':
      return (entry as HistoryDiagnosis).condition_name;
    case 'medication':
      return (entry as HistoryMedication).medication_name;
    case 'procedure':
      return (entry as HistoryProcedure).procedure_name;
  }
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getEntryDates(entry: HistoryEntry, entryType: HistoryEntryType): string {
  switch (entryType) {
    case 'diagnosis': {
      const d = entry as HistoryDiagnosis;
      if (d.onset_date && d.resolution_date) {
        return `${formatDate(d.onset_date)} - ${formatDate(d.resolution_date)}`;
      }
      if (d.onset_date) return `Since ${formatDate(d.onset_date)}`;
      return '';
    }
    case 'medication': {
      const m = entry as HistoryMedication;
      if (m.start_date && m.end_date) {
        return `${formatDate(m.start_date)} - ${formatDate(m.end_date)}`;
      }
      if (m.start_date) return `Since ${formatDate(m.start_date)}`;
      return '';
    }
    case 'procedure': {
      const p = entry as HistoryProcedure;
      if (p.procedure_date) return formatDate(p.procedure_date);
      return '';
    }
  }
}

function getMedicationDetails(entry: HistoryMedication): string | null {
  const parts: string[] = [];
  if (entry.dose) parts.push(entry.dose);
  if (entry.frequency) parts.push(entry.frequency);
  if (entry.route) parts.push(entry.route);
  return parts.length > 0 ? parts.join(' | ') : null;
}

function getProcedureDetails(entry: HistoryProcedure): string | null {
  const parts: string[] = [];
  if (entry.procedure_type) {
    const typeLabels: Record<string, string> = {
      surgery: 'Surgery',
      procedure: 'Procedure',
      diagnostic: 'Diagnostic',
      therapeutic: 'Therapeutic',
    };
    parts.push(typeLabels[entry.procedure_type] || entry.procedure_type);
  }
  if (entry.outcome) parts.push(`Outcome: ${entry.outcome}`);
  return parts.length > 0 ? parts.join(' | ') : null;
}

function groupSourcePages(sourcePages: SourcePage[]): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  for (const sp of sourcePages) {
    const pages = groups.get(sp.document_id) || [];
    if (!pages.includes(sp.page)) pages.push(sp.page);
    groups.set(sp.document_id, pages);
  }
  // Sort pages within each group
  for (const [docId, pages] of groups) {
    groups.set(docId, pages.sort((a, b) => a - b));
  }
  return groups;
}

export function HistoryEntryCard({
  entry,
  entryType,
  onEdit,
  onVerify,
  onDelete,
  onUpdateNotes,
  onStrikeOut,
  onUndoStrikeOut,
  onPageClick,
}: HistoryEntryCardProps) {
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState(entry.coordinator_notes || '');

  const name = getEntryName(entry, entryType);
  const status = entry.status;
  const dates = getEntryDates(entry, entryType);
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.uncertain;
  // Guard against null/undefined/out-of-enum confidence — an unguarded lookup or
  // .charAt() throws and blanks the whole view via MHRErrorBoundary.
  const conf = entry.confidence || 'medium';
  const confidenceColor = CONFIDENCE_COLORS[entry.confidence] || CONFIDENCE_COLORS.medium;
  const isVerified = !!entry.verified_by;
  const isStruckOut = !!entry.struck_out;
  const sourceDocCount = entry.source_document_ids?.length || 0;
  const hasMergeHistory = entry.merged_from_ids && entry.merged_from_ids.length > 0;
  const linkedDxCount = entryType === 'medication'
    ? (entry as HistoryMedication).linked_diagnosis_ids?.length || 0
    : 0;
  const groupedPages = groupSourcePages(entry.source_pages || []);

  let additionalDetails: string | null = null;
  if (entryType === 'medication') {
    additionalDetails = getMedicationDetails(entry as HistoryMedication);
  } else if (entryType === 'procedure') {
    additionalDetails = getProcedureDetails(entry as HistoryProcedure);
  }

  let codes: string[] = [];
  if (entryType === 'diagnosis') {
    codes = (entry as HistoryDiagnosis).icd_codes || [];
  } else if (entryType === 'procedure') {
    codes = (entry as HistoryProcedure).cpt_codes || [];
  }

  const handleSaveNotes = () => {
    onUpdateNotes?.(entry, notesValue);
    setEditingNotes(false);
  };

  return (
    <div
      className={`rounded-xl border p-4 hover:shadow-md transition-shadow ${
        isStruckOut
          ? 'opacity-60 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40'
          : isVerified
            ? 'bg-white dark:bg-gray-800 border-green-200 dark:border-green-800'
            : entry.is_merged_duplicate
              ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 opacity-60'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Merge duplicate indicator */}
      {entry.is_merged_duplicate && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
          <GitMerge size={12} />
          Merged into another entry
        </div>
      )}

      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`text-sm font-medium text-gray-900 dark:text-gray-100 truncate ${isStruckOut ? 'line-through' : ''}`}>{name}</h4>
            {entry.is_ai_generated && (
              <span className="inline-flex items-center gap-0.5 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded">
                <Bot size={10} />
                AI
              </span>
            )}
            {hasMergeHistory && (
              <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-1.5 py-0.5 rounded">
                <GitMerge size={10} />
                Merged
              </span>
            )}
          </div>
          {additionalDetails && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{additionalDetails}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isVerified && (
            <span
              className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-2 py-1 rounded-full"
              title={`Verified${entry.verified_at ? ` on ${formatDate(entry.verified_at)}` : ''}`}
            >
              <CheckCircle size={12} />
              Verified
            </span>
          )}
          <span
            className={`inline-block text-xs font-medium px-2 py-1 rounded-full ${statusColor.bg} ${statusColor.text}`}
          >
            {STATUS_LABELS[status] || status}
          </span>
        </div>
      </div>

      {/* Dates row */}
      {dates && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{dates}</p>}

      {/* Codes row */}
      {codes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {codes.map((code, i) => (
            <span
              key={i}
              className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-1.5 py-0.5 rounded font-mono"
            >
              {code}
            </span>
          ))}
        </div>
      )}

      {/* Struck-out reason */}
      {isStruckOut && entry.struck_out_reason && (
        <p className="text-xs italic text-gray-500 dark:text-gray-400 mb-2">
          Struck out: {entry.struck_out_reason}
        </p>
      )}

      {/* Multi-document source + diagnosis links + page refs */}
      <div className="flex flex-wrap items-center gap-3 mb-2">
        {sourceDocCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <FileText size={12} />
            From {sourceDocCount} document{sourceDocCount !== 1 ? 's' : ''}
          </span>
        )}
        {linkedDxCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400">
            <Link2 size={12} />
            {linkedDxCount} linked diagnos{linkedDxCount !== 1 ? 'es' : 'is'}
          </span>
        )}
        {groupedPages.size > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            {Array.from(groupedPages.entries()).map(([docId, pages], idx) => (
              <span key={docId} className="inline-flex items-center gap-0.5">
                {idx > 0 && <span className="text-gray-300 dark:text-gray-600 mx-0.5">|</span>}
                <FileText size={10} className="text-gray-400 dark:text-gray-500" />
                {pages.map((p, pIdx) => (
                  <span key={p}>
                    {pIdx > 0 && <span>, </span>}
                    <button
                      onClick={() => onPageClick?.(docId, p)}
                      className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
                    >
                      p.{p}
                    </button>
                  </span>
                ))}
              </span>
            ))}
          </span>
        )}
      </div>

      {/* Coordinator notes */}
      {(entry.coordinator_notes || editingNotes) && (
        <div className="mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 mb-1">
            <MessageSquare size={12} />
            Coordinator Notes
          </div>
          {editingNotes ? (
            <div className="flex gap-2">
              <textarea
                value={notesValue}
                onChange={e => setNotesValue(e.target.value)}
                className="flex-1 text-xs text-gray-700 dark:text-gray-300 border border-amber-300 dark:border-amber-700 rounded p-1.5 resize-none bg-white dark:bg-gray-700"
                rows={2}
                autoFocus
              />
              <div className="flex flex-col gap-1">
                <button
                  onClick={handleSaveNotes}
                  className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                  title="Save"
                >
                  <Save size={14} />
                </button>
                <button
                  onClick={() => {
                    setEditingNotes(false);
                    setNotesValue(entry.coordinator_notes || '');
                  }}
                  className="p-1 text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                  title="Cancel"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ) : (
            <p
              className="text-xs text-amber-800 dark:text-amber-300 cursor-pointer hover:text-amber-900 dark:hover:text-amber-200"
              onClick={() => onUpdateNotes && setEditingNotes(true)}
            >
              {entry.coordinator_notes}
            </p>
          )}
        </div>
      )}

      {/* Confidence, source, and actions row */}
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <div
            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${confidenceColor.bg} ${confidenceColor.text}`}
          >
            {conf === 'low' && <AlertTriangle size={12} />}
            {conf.charAt(0).toUpperCase() + conf.slice(1)}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          {!isStruckOut && !entry.coordinator_notes && onUpdateNotes && (
            <button
              onClick={() => setEditingNotes(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Add notes"
            >
              <MessageSquare size={12} />
              Note
            </button>
          )}
          {!isStruckOut && onEdit && (
            <button
              onClick={() => onEdit(entry)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Edit entry"
            >
              <Edit2 size={12} />
              Edit
            </button>
          )}
          {!isStruckOut && !isVerified && onVerify && (
            <button
              onClick={() => onVerify(entry)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
              title="Verify entry"
            >
              <Eye size={12} />
              Verify
            </button>
          )}
          {!isStruckOut && onStrikeOut && !entry.is_merged_duplicate && (
            <button
              onClick={() => onStrikeOut(entry)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
              title="Strike out"
            >
              <Strikethrough size={12} />
            </button>
          )}
          {isStruckOut && onUndoStrikeOut && (
            <button
              onClick={() => onUndoStrikeOut(entry)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="Undo strike out"
            >
              <Undo2 size={12} />
              Undo
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(entry)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Delete entry"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
